import { StripeSubscriptionItemInput } from '../../../../shared/graphql/graphql';
import { getCurrencyForAreaUnit } from '../../../../shared/helpers/functions/currency';
import usePriceConfig from '../../../../shared/features/priceConfig/hooks/usePriceConfig';
import { logError } from '../../../../shared/helpers/functions/api';
import rootStore from '../../rootStore/RootStore';

export default function useStripeSubscriptionPreview() {
  const { uiStateStore, userStore, subscriptionPreviewStore, areaUnitStore } =
    rootStore;
  const { isLoaded: isConfigLoaded, config } = usePriceConfig(rootStore);
  const { annualPlanValid } = uiStateStore;
  const { areaUnit } = areaUnitStore;

  const currency = getCurrencyForAreaUnit(areaUnit);

  const generateSubscriptionPreview = async ({
    annual,
    monthly,
    promocode,
  }: {
    annual?: {
      area: string;
      seats: string;
      planet: string;
    };
    monthly?: boolean;
    promocode?: string;
  }) => {
    if (!isConfigLoaded || subscriptionPreviewStore.inProgress) {
      return null;
    }

    const items: StripeSubscriptionItemInput[] = [];
    const userCountry =
      userStore.selectedCountry || userStore.userDataCountryInfo?.alpha2;
    const customerId = userStore.combinedData.stripeCustomer?.id;

    if (!customerId) {
      return null;
    }

    if (annual) {
      if (!annualPlanValid) {
        return null;
      }

      const annualArea = parseInt(annual.area, 10);
      const annualSeats = parseInt(annual.seats, 10);
      const annualPlanetArea = parseInt(annual.planet, 10);

      const annualPrices = config?.AnnualSubscription;
      const areaPriceId =
        annualPrices?.FullAccessToEssentialFunctionalities?.prices[currency]
          ?.priceId;
      const seatPriceId =
        annualPrices?.AdditionalUserSeat?.prices[currency]?.priceId;
      const planetPriceId =
        annualPrices?.PlanetImagery?.prices[currency]?.priceId;

      if (!areaPriceId || !seatPriceId || !planetPriceId) {
        logError(
          new Error(
            '[useStripeSubscriptionPreview][generateSubscriptionPreview] Annual plan prices are missing.',
          ),
        );
        return null;
      }

      items.push({ price: areaPriceId, quantity: annualArea });

      if (annualSeats > 0) {
        items.push({ price: seatPriceId, quantity: annualSeats });
      }

      if (annualPlanetArea > 0) {
        items.push({ price: planetPriceId, quantity: annualPlanetArea });
      }
    } else if (monthly) {
      const planConfig = config?.MonthlyPayAsYouGo;

      if (!planConfig?.BaseMonthlyFee?.prices[currency]?.priceId) {
        logError(
          new Error(
            '[useStripeSubscriptionPreview][generateSubscriptionPreview] Monthly base fee price is missing.',
          ),
        );
        return null;
      }

      Object.values(planConfig).forEach((product) => {
        const priceId = product?.prices[currency]?.priceId;

        if (priceId) {
          items.push({ price: priceId });
        }
      });
    }

    return subscriptionPreviewStore.generateSubscriptionPreview({
      customerId,
      country: userCountry,
      items,
      promocode,
    });
  };

  return {
    generateSubscriptionPreview,
    preview: subscriptionPreviewStore.preview,
  };
}
